import { isPurchaseProcess, resolveLatestProcessName } from '../transactions/transaction';

/**
 * How a vehicle sale is settled on this marketplace.
 *
 * Settlement is manual: the buyer and seller are connected on the platform, and
 * the Skip team and its dealer partners arrange payment off the platform —
 * dealer partners coordinate any loan or lien payoff, and the seller's
 * remaining proceeds are paid by cheque once the sale is confirmed. Skip does
 * not advance capital and does not take vehicle-sale funds through checkout.
 *
 * Two things follow, and both are answered here so there is one rule:
 *
 *  - A seller is never asked for Stripe payout details to publish a vehicle.
 *    Payout details exist to receive checkout funds, and there are none.
 *  - A buyer is never sent to card checkout for a vehicle. A listing that is
 *    still on a purchase process (every listing created before this rule) is
 *    shown as "contact the seller", which uses the purchase process's own
 *    `transition/inquire`, so no listing data has to change for it to work.
 *
 * Platform fees and subscriptions (listing plans, dealer subscriptions) are
 * separate Stripe flows and are not affected.
 *
 * Automated settlement would be a separate decision; flip this one constant
 * and every caller follows.
 */
export const VEHICLE_SETTLEMENT = 'manual';

/** @returns {boolean} true while vehicle sales are settled off the platform */
export const isManualVehicleSettlement = () => VEHICLE_SETTLEMENT === 'manual';

/**
 * Whether sellers are offered Stripe Connect payout onboarding.
 *
 * Payout details exist to receive checkout funds. Under manual settlement there are none: dealer
 * partners settle the sale, pay any loan or lien, and send the seller's proceeds by cheque. So the
 * account tab, listing warnings and onboarding form are not shown, and the old payout URLs explain
 * how sellers are paid instead. A Stripe account a seller connected earlier is left as it is.
 *
 * Platform billing (listing fees, dealer subscriptions) is a separate Stripe flow and unaffected.
 *
 * @returns {boolean}
 */
export const isPayoutOnboardingOffered = () => !isManualVehicleSettlement();

/**
 * Whether buyers are asked to save a card for vehicle checkout (Sharetribe's "Payment methods").
 * Under manual settlement nothing charges that card, so the account tab gives way to Billing, which
 * covers what Stripe is actually used for: listing plans and platform subscriptions.
 *
 * @returns {boolean}
 */
export const isVehicleCheckoutOffered = () => !isManualVehicleSettlement();

/** The process name a listing's publicData points at, resolved to its latest name. */
const processNameOf = listing => {
  const alias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  return alias ? resolveLatestProcessName(alias.split('/')[0]) : null;
};

/**
 * Whether a buyer should be offered "contact the seller" instead of checkout for
 * this listing. True for purchase-process listings while settlement is manual.
 *
 * @param {Object} listing
 * @returns {boolean}
 */
export const isContactOnlyVehicleListing = listing =>
  isManualVehicleSettlement() && isPurchaseProcess(processNameOf(listing));

/**
 * Same rule for a process name the caller already has (OrderPanel).
 *
 * @param {string} processName
 * @returns {boolean}
 */
export const isContactOnlyProcess = processName =>
  isManualVehicleSettlement() && isPurchaseProcess(resolveLatestProcessName(processName));

const INQUIRE = 'transition/inquire';

/**
 * Whether checkout page data describes a vehicle order that must not reach
 * Stripe: the listing is on a purchase process and payment has not started —
 * either there is no transaction yet (a typed or bookmarked /checkout URL), or
 * the transaction is still the buyer's inquiry (the purchase process would
 * otherwise let it continue to request-payment-after-inquiry).
 *
 * A transaction that already moved past the inquiry is never redirected, so its
 * history stays reachable.
 *
 * @param {Object} pageData { transaction, listing } as CheckoutPage holds them
 * @returns {boolean}
 */
export const isNewContactOnlyOrder = pageData => {
  const tx = pageData?.transaction;
  const paymentNotStarted = !tx?.id || tx?.attributes?.lastTransition === INQUIRE;
  return paymentNotStarted && isContactOnlyVehicleListing(pageData?.listing);
};
